import {OverlayView, Polygon} from '@react-google-maps/api'
import {
  BiAlarmExclamation,
  BiArea,
  BiBookOpen,
  BiError,
  BiShoppingBag,
  BiTrain,
} from 'react-icons/bi'
import {BsFillTrainLightrailFrontFill} from 'react-icons/bs'

type MarkerProps = {
  geom?: any
  geometria?: any
  icone?: string
  nome?: string

  servico?: string
  service?: string

  resultado?: {itens?: any[]}
  itens?: any[]
}

function parseGeoJson(geom: unknown) {
  if (!geom) {
    return null
  }
  if (typeof geom === 'string') {
    try {
      return JSON.parse(geom)
    } catch {
      return null
    }
  }
  if (typeof geom === 'object') {
    return geom
  }
  return null
}

function coordinatesToPath(coords: number[][]) {
  return coords.map((point) => ({lat: point[1], lng: point[0]}))
}

function polygonToPaths(coords: number[][][]) {
  return coords.map((ring) => coordinatesToPath(ring))
}

function getColorByService(servico: string) {
  switch (servico) {
    case 'metro':
      return '#e8e8e8'
    case 'trem':
      return '#e8e8e8'
    case 'metro-planejado':
      return '#e8e8e8'
    case 'equipamentos-educacao':
      return '#4fd237'
    case 'shopping':
      return '#7b4a12'
    case 'area-alagamento':
      return '#b42318'
    case 'risco-geologico':
      return '#b42318'
    case 'area-vulnerabilidade':
      return '#6f2dbd'
    default:
      return '#344054'
  }
}

function normalizeItens(markers: MarkerProps[]) {
  const itens = []
  markers.forEach((entry) => {
    if (entry?.resultado?.itens && entry.resultado.itens.length > 0) {
      entry.resultado.itens.forEach((item) => {
        itens.push({
          ...item,
          servico: entry.servico,
          nome: item.nome || item.nome_estacao || entry.nome,
          icone: item.icone || entry.icone,
          color: getColorByService(entry.servico),
        })
      })
      return
    }
    // if (entry?.itens && entry.itens.length > 0) {
    //   entry.itens.forEach((item) => {
    //     itens.push({
    //       ...item,
    //       servico: item.servico || entry.servico || entry.service,
    //       nome: item.nome || entry.nome,
    //       icone: item.icone || entry.icone,
    //     })
    //   })
    //   return
    // }
    if (entry?.geom || entry?.geometria) {
      itens.push(entry)
    }
  })
  return itens
}

function getServiceKey(item: MarkerProps) {
  return (item?.nome || item?.nome || '').toString().toLowerCase()
}

function renderServiceIcon(item: MarkerProps) {
  const key = getServiceKey(item)
  if (item.servico === 'metro' || key.includes('metrô')) {
    return <BiTrain size={20} color='#545454' />
  }
  if (item.servico === 'trem' || key.includes('trem')) {
    return <BsFillTrainLightrailFrontFill size={20} color='#545454' />
  }
  if (item.servico === 'metro-planejado' || key.includes('metrô')) {
    return <BiTrain size={20} color='#ed0d0d' />
  }
  if (item.servico === 'equipamentos-educacao' || key.includes('educa')) {
    return <BiBookOpen size={20} color='#ffffff' />
  }
  if (item.servico === 'shopping' || key.includes('shopping')) {
    return <BiShoppingBag size={20} color='#7b4a12' />
  }
  if (item.servico === 'area-alagamento' || key.includes('risco')) {
    return <BiAlarmExclamation size={20} color='#b42318' />
  }
  if (item.servico === 'risco-geologico' || key.includes('risco')) {
    return <BiAlarmExclamation size={20} color='#b42318' />
  }
  if (item.servico === 'area-vulnerabilidade' || key.includes('vulner')) {
    return <BiArea size={20} color='#6f2dbd' />
  }
  return <BiError size={20} color='#344054' />
}

export function MakerServicesInfra({marker}: {marker: MarkerProps[]}) {
  const itens = normalizeItens(marker || [])

  return (
    <>
      {itens.map((item, index) => {
        const geom = parseGeoJson(item.geom || item.geometria)
        if (!geom || !geom.type || !geom.coordinates) {
          return null
        }
        const labelText = item.nome.toString().split('-')[0]
        if (geom.type === 'Point') {
          return (
            <OverlayView
              key={`marker-${index}`}
              position={{lat: geom.coordinates[1], lng: geom.coordinates[0]}}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <>
                <div
                  title={labelText}
                  style={{
                    transform: 'translate(-50%, -50%)',
                    background: item.color || '#6e6e6e',
                    border: '1px solid #d0d5dd',
                    borderRadius: '999px',
                    padding: '5px',
                    width: '32px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  }}
                >
                  {renderServiceIcon(item)}
                </div>
                <div
                  title={labelText}
                  style={{
                    transform: 'translate(-50%, -50%)',
                    background: item.color || '#6e6e6e',
                    border: '1px solid #d0d5dd',
                    padding: '5px',
                    width: '100px',
                    fontSize: '16px',
                    textAlign: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  }}
                >
                  {item.nome}
                </div>
              </>
            </OverlayView>
          )
        }
        if (geom.type === 'MultiPoint') {
          return geom.coordinates.map((coordinate, pointIndex) => (
            <OverlayView
              key={`multipoint-${index}-${pointIndex}`}
              position={{lat: coordinate[1], lng: coordinate[0]}}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <>
                <div
                  title={labelText}
                  style={{
                    transform: 'translate(-50%, -50%)',
                    background: item.color || '#6e6e6e',
                    border: '1px solid #d0d5dd',
                    borderRadius: '999px',
                    padding: '5px',
                    width: '32px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  }}
                >
                  {renderServiceIcon(item)}
                </div>
                <div
                  title={labelText}
                  style={{
                    transform: 'translate(-50%, -50%)',
                    background: '#f0f0f0',
                    border: '1px solid #d0d5dd',
                    padding: '5px',
                    width: '100px',
                    fontSize: '12px',
                    textAlign: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  }}
                >
                  {item.nome}
                </div>
              </>
            </OverlayView>
          ))
        }
        if (geom.type === 'Polygon') {
          const paths = polygonToPaths(geom.coordinates)
          return (
            <Polygon
              key={`polygon-${index}`}
              paths={paths}
              options={{
                fillOpacity: 0.2,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: getColorByService(item.servico || '#ff0000'),
              }}
            />
          )
        }
        if (geom.type === 'MultiPolygon') {
          return geom.coordinates.map((polygon, polygonIndex) => {
            const paths = polygonToPaths(polygon)
            return (
              <Polygon
                key={`multipolygon-${index}-${polygonIndex}`}
                paths={paths}
                options={{
                  fillOpacity: 0.2,
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: getColorByService(item.servico || '#ff0000'),
                }}
              />
            )
          })
        }
        return null
      })}
    </>
  )
}
